import { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLoaderData, useParams, redirect } from "react-router-dom";
import { motion } from "framer-motion";
import {
  HiUser,
  HiEnvelope,
  HiIdentification,
  HiBanknotes,
  HiLockClosed,
  HiPencilSquare,
  HiCheck,
  HiExclamationTriangle,
} from "react-icons/hi2";

import adminUi from "../UI/AdminUi.module.css";
import { getOneAccount } from "../../api/api";
import Spinner from "../UI/Spinner";
import { alertActions } from "../../store/alert-slice";
import { updateAccount } from "../../api/api";
import { getAuthToken } from "../../util/authToken";

const AccountsForm = () => {
  const [showSpinner, setShowSpinner] = useState(false);
  const dispatch = useDispatch();
  const res = useLoaderData();
  const account = res?.data?.user;
  const params = useParams();
  const nameRef = useRef();
  const emailRef = useRef();
  const accountRef = useRef();
  const balanceRef = useRef();
  const blockRef = useRef();

  if (!account) {
    return (
      <div className={adminUi.page}>
        <h1 className={adminUi.pageTitle}>
          <HiExclamationTriangle
            className={adminUi.pageTitleIcon}
            aria-hidden
          />
          Account unavailable
        </h1>
        <p className={adminUi.pageLead}>
          This account could not be loaded. Return to the list and try again.
        </p>
      </div>
    );
  }

  const blockingDefault = account.active ? "false" : "true";

  const updateHandler = async (e) => {
    e.preventDefault();
    setShowSpinner(true);

    const blockVal = blockRef.current.value;
    const data = {
      name: nameRef.current.value,
      email: emailRef.current.value,
      accountNumber: accountRef.current.value,
      balance: balanceRef.current.value,
      active: blockVal === "false",
    };

    const jwt = getAuthToken();
    const resUpdate = await updateAccount(jwt, data, params.id);

    if (resUpdate.status === "success") {
      dispatch(
        alertActions.setState({
          message: resUpdate.message,
          status: resUpdate.status,
        })
      );
    } else {
      dispatch(
        alertActions.setState({
          message: resUpdate.message,
          status: "error",
        })
      );
    }

    setShowSpinner(false);
  };

  return (
    <div className={adminUi.page}>
      <h1 className={adminUi.pageTitle}>
        <HiPencilSquare className={adminUi.pageTitleIcon} aria-hidden />
        Update account
      </h1>
      <p className={adminUi.pageLead}>
        Edit profile and balance. Use blocking to stop sign-in while keeping the
        record on file.
      </p>
      <motion.form
        className={adminUi.formCard}
        onSubmit={updateHandler}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
      >
        {showSpinner && <Spinner />}
        <h2 className={adminUi.formTitle}>
          <HiUser className={adminUi.formTitleIcon} aria-hidden />
          Account details
        </h2>
        <div className={adminUi.field}>
          <label htmlFor="acct-name">Name</label>
          <div className={adminUi.inputWrap}>
            <HiUser className={adminUi.fieldIcon} aria-hidden />
            <input
              id="acct-name"
              name="name"
              type="text"
              defaultValue={account.name}
              ref={nameRef}
            />
          </div>
        </div>
        <div className={adminUi.field}>
          <label htmlFor="acct-email">Email</label>
          <div className={adminUi.inputWrap}>
            <HiEnvelope className={adminUi.fieldIcon} aria-hidden />
            <input
              id="acct-email"
              name="email"
              type="email"
              defaultValue={account.email}
              ref={emailRef}
            />
          </div>
        </div>
        <div className={adminUi.field}>
          <label htmlFor="acct-number">Account number</label>
          <div className={adminUi.inputWrap}>
            <HiIdentification className={adminUi.fieldIcon} aria-hidden />
            <input
              id="acct-number"
              name="accountNumber"
              type="text"
              defaultValue={account.accountNumber}
              ref={accountRef}
            />
          </div>
        </div>
        <div className={adminUi.field}>
          <label htmlFor="acct-balance">Balance</label>
          <div className={adminUi.inputWrap}>
            <HiBanknotes className={adminUi.fieldIcon} aria-hidden />
            <input
              id="acct-balance"
              name="balance"
              type="text"
              inputMode="decimal"
              defaultValue={account.balance}
              ref={balanceRef}
            />
          </div>
        </div>
        <div className={adminUi.field}>
          <label htmlFor="acct-block">Blocking account</label>
          <span className={adminUi.fieldHint}>
            true blocks the customer; false keeps the account active.
          </span>
          <div className={adminUi.selectWrap}>
            <HiLockClosed className={adminUi.fieldIcon} aria-hidden />
            <select
              id="acct-block"
              name="blocking"
              ref={blockRef}
              defaultValue={blockingDefault}
            >
              <option value="false">false</option>
              <option value="true">true</option>
            </select>
          </div>
        </div>
        <div className={adminUi.actions}>
          <button className={adminUi.btnPrimary} type="submit">
            <HiCheck className={adminUi.btnPrimaryIcon} aria-hidden />
            Save changes
          </button>
        </div>
      </motion.form>
    </div>
  );
};

export default AccountsForm;

export const loader = async ({ params }) => {
  const jwt = getAuthToken();
  if (!jwt) {
    throw redirect("/login");
  }
  const res = await getOneAccount(jwt, params.id);
  if (res.status !== "success" || !res.data?.user) {
    throw redirect("/accounts");
  }
  return res;
};
